import { useState } from 'react';
import { FiUser, FiCheckCircle, FiClock, FiAlertCircle, FiTrendingUp, FiTrendingDown, FiMinus, FiFilter, FiHome } from 'react-icons/fi';
import { IoBedOutline } from 'react-icons/io5';
import { GiBroom } from 'react-icons/gi';
import { MdMeetingRoom } from 'react-icons/md';
import { calculateModifiedCleaningTime } from '../../utils/timeCalculator';

const StaffCleaningOrdersView = ({ orders, users, rooms, schedules = [] }) => {
  const [selectedFloor, setSelectedFloor] = useState('all');

  // Función para formatear horas decimales a formato HH:MM
  const formatHours = (decimalHours) => {
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}h`;
  };

  // Función para extraer el número de planta del número de habitación
  const getFloorFromRoomNumber = (roomNumber) => {
    if (!roomNumber) return null;
    // Extraer los primeros dígitos del número de habitación
    // Ej: "101" -> "1", "201" -> "2", "1001" -> "10", "1A" -> "1"
    const match = roomNumber.toString().match(/^(\d+)/);
    if (match) {
      const digits = match[1];
      // Si tiene 3 dígitos o menos, el primer dígito es la planta
      // Si tiene 4 o más dígitos, los primeros 2 dígitos son la planta
      if (digits.length <= 3) {
        return digits.charAt(0);
      } else {
        return digits.substring(0, 2);
      }
    }
    return null;
  };

  // Obtener lista única de plantas de todas las habitaciones
  const availableFloors = [...new Set(
    rooms
      .map(room => getFloorFromRoomNumber(room.number))
      .filter(floor => floor !== null)
  )].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  // Filtrar órdenes por planta si hay una planta seleccionada
  const filteredOrders = selectedFloor === 'all'
    ? orders
    : orders.filter(order => {
        const room = rooms.find(r => r.id === order.room_id);
        if (!room) return false;
        const floor = getFloorFromRoomNumber(room.number);
        return floor === selectedFloor;
      });

  // Agrupar órdenes filtradas por personal
  const ordersByStaff = {};

  // Inicializar con personal de limpieza
  users.forEach(user => {
    ordersByStaff[user.id] = {
      staffId: user.id,
      staffName: user.name,
      roomOrders: [],      // Órdenes de habitaciones
      commonSpaceOrders: [] // Órdenes de espacios comunes
    };
  });

  // Agrupar órdenes filtradas por personal asignado, separando habitaciones y espacios comunes
  filteredOrders.forEach(order => {
    if (order.cleaning_staff_id && ordersByStaff[order.cleaning_staff_id]) {
      if (order.common_space_id) {
        // Es un espacio común
        ordersByStaff[order.cleaning_staff_id].commonSpaceOrders.push(order);
      } else {
        // Es una habitación
        const room = rooms.find(r => r.id === order.room_id);
        ordersByStaff[order.cleaning_staff_id].roomOrders.push({
          ...order,
          room: room
        });
      }
    }
  });

  // Filtrar solo el personal que tiene órdenes asignadas y ordenar las habitaciones
  const staffWithOrders = Object.values(ordersByStaff)
    .filter(staff => staff.roomOrders.length > 0 || staff.commonSpaceOrders.length > 0)
    .map(staff => {
      // Calcular horas estimadas de las órdenes de habitaciones (con modificador de tipo)
      const roomEstimatedHours = staff.roomOrders.reduce((total, order) => {
        const room = order.room;
        if (room && room.cleaning_time_minutes) {
          // Apply cleaning type modifier if available
          const cleaningType = order.cleaning_type_obj;
          const modifiedTime = calculateModifiedCleaningTime(
            room.cleaning_time_minutes,
            cleaningType?.time_modifier_type || 'none',
            cleaningType?.time_modifier_value || 0
          );
          return total + (modifiedTime / 60);
        }
        return total;
      }, 0);

      // Calcular horas estimadas de los espacios comunes (tiempo base directo, sin modificadores)
      const commonSpaceEstimatedHours = staff.commonSpaceOrders.reduce((total, order) => {
        if (order.common_space && order.common_space.cleaning_time_minutes) {
          return total + (order.common_space.cleaning_time_minutes / 60);
        }
        return total;
      }, 0);

      const estimatedHours = roomEstimatedHours + commonSpaceEstimatedHours;

      // Obtener horas trabajadas del cuadrante para este empleado
      const schedule = schedules.find(s => s.employee_id === staff.staffId);
      const scheduledHours = schedule ? schedule.hours : 0;

      // Calcular diferencia
      const hoursDifference = scheduledHours - estimatedHours;

      // Total de órdenes para contadores
      const allOrders = [...staff.roomOrders, ...staff.commonSpaceOrders];

      return {
        ...staff,
        estimatedHours,
        scheduledHours,
        hoursDifference,
        totalOrders: allOrders.length,
        completedOrders: allOrders.filter(o => o.cleaning_timestamp && o.checkout_timestamp).length,
        pendingOrders: allOrders.filter(o => !o.cleaning_timestamp || !o.checkout_timestamp).length,
        roomOrders: staff.roomOrders.sort((a, b) => {
          const roomNumA = a.room?.number || '';
          const roomNumB = b.room?.number || '';
          return roomNumA.localeCompare(roomNumB, undefined, { numeric: true, sensitivity: 'base' });
        }),
        commonSpaceOrders: staff.commonSpaceOrders.sort((a, b) => {
          const nameA = a.common_space?.name || '';
          const nameB = b.common_space?.name || '';
          return nameA.localeCompare(nameB);
        })
      };
    });

  // Función para obtener el color del estado
  const getStatusColor = (order) => {
    // Si tiene ambos timestamps completados
    if (order.cleaning_timestamp && order.checkout_timestamp) {
      return 'bg-green-100 text-green-800';
    }
    // Si tiene al menos uno completado
    if (order.cleaning_timestamp || order.checkout_timestamp) {
      return 'bg-yellow-100 text-yellow-800';
    }
    // Pendiente
    return 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (order) => {
    if (order.cleaning_timestamp) {
      return 'Completado';
    }
    if (order.checkout_timestamp) {
      return 'Check-out';
    }
    return 'Pendiente';
  };

  if (staffWithOrders.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <FiUser className="h-5 w-5 text-gray-600 mr-2" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-gray-900">
            Órdenes por Personal
          </h2>
        </div>
        <div className="text-center text-gray-500 py-8">
          <FiAlertCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" aria-hidden="true" />
          <p>No hay órdenes asignadas a ningún personal de limpieza</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center">
          <FiUser className="h-5 w-5 text-gray-600 mr-2" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-gray-900">
            Órdenes por Personal de Limpieza
          </h2>
        </div>

        {/* Filtro por planta */}
        {availableFloors.length > 0 && (
          <div className="flex items-center gap-2">
            <FiFilter className="h-4 w-4 text-gray-500" aria-hidden="true" />
            <label htmlFor="floor-filter" className="text-sm font-medium text-gray-700">
              Planta:
            </label>
            <select
              id="floor-filter"
              value={selectedFloor}
              onChange={(e) => setSelectedFloor(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Todas las plantas</option>
              {availableFloors.map(floor => (
                <option key={floor} value={floor}>
                  Planta {floor}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {staffWithOrders.map((staff) => (
          <div key={staff.staffId} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Header del personal */}
            <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
              <div className="flex flex-col space-y-2">
                {/* Nombre y contadores */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center min-w-0">
                    <div className="bg-blue-100 rounded-full p-1.5 mr-2 flex-shrink-0">
                      <FiUser className="h-3 w-3 text-blue-600" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm text-gray-900 truncate">{staff.staffName}</h3>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 text-xs flex-shrink-0 ml-2">
                    <div className="flex items-center">
                      <FiCheckCircle className="h-3 w-3 text-green-600 mr-1" aria-hidden="true" />
                      <span className="text-gray-700">
                        {staff.completedOrders}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <FiClock className="h-3 w-3 text-yellow-600 mr-1" aria-hidden="true" />
                      <span className="text-gray-700">
                        {staff.pendingOrders}
                      </span>
                    </div>
                    <span className="text-gray-600">
                      {staff.totalOrders} total
                    </span>
                  </div>
                </div>

                {/* Horas trabajadas vs estimadas */}
                {staff.scheduledHours > 0 && (
                  <div className="flex items-center justify-between text-xs px-1">
                    <div className="flex items-center space-x-3">
                      <span className="text-gray-600">
                        Turno: <span className="font-semibold text-gray-900">{formatHours(staff.scheduledHours)}</span>
                      </span>
                      <span className="text-gray-600">
                        Estimado: <span className="font-semibold text-gray-900">{formatHours(staff.estimatedHours)}</span>
                      </span>
                    </div>
                    <div className="flex items-center">
                      {staff.hoursDifference > 0.5 ? (
                        // Tiene horas libres
                        <div className="flex items-center text-green-600">
                          <FiTrendingDown className="h-3 w-3 mr-1" aria-hidden="true" />
                          <span className="font-semibold">{formatHours(Math.abs(staff.hoursDifference))} libres</span>
                        </div>
                      ) : staff.hoursDifference < -0.5 ? (
                        // Está sobrecargado
                        <div className="flex items-center text-red-600">
                          <FiTrendingUp className="h-3 w-3 mr-1" aria-hidden="true" />
                          <span className="font-semibold">{formatHours(Math.abs(staff.hoursDifference))} exceso</span>
                        </div>
                      ) : (
                        // Equilibrado
                        <div className="flex items-center text-blue-600">
                          <FiMinus className="h-3 w-3 mr-1" aria-hidden="true" />
                          <span className="font-semibold">Equilibrado</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Tabla de habitaciones */}
            {staff.roomOrders.length > 0 && (
              <div className="overflow-x-auto">
                <div className="flex items-center px-2 py-1 bg-blue-50 border-b border-gray-200">
                  <FiHome className="h-3 w-3 text-blue-600 mr-1" aria-hidden="true" />
                  <span className="text-xs font-medium text-blue-700">Habitaciones ({staff.roomOrders.length})</span>
                </div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Hab.
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Pl.
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Camas
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Limpieza
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {staff.roomOrders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          <div className="flex items-center justify-center">
                            <div className="flex-shrink-0 h-6 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <span className="text-xs font-medium text-blue-800">
                                {order.room?.number || 'N/A'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          <span className="text-xs text-gray-600">
                            {getFloorFromRoomNumber(order.room?.number) || '-'}
                          </span>
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center">
                            <IoBedOutline className="h-3 w-3 text-gray-400 mr-1 flex-shrink-0" aria-hidden="true" />
                            <span className="text-xs text-gray-900 truncate">
                              {order.bed_disposition?.name || order.bed_disposition_name || 'N/E'}
                            </span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center">
                            <GiBroom className="h-3 w-3 text-gray-400 mr-1 flex-shrink-0" aria-hidden="true" />
                            <span className="text-xs text-gray-900 truncate">
                              {order.cleaning_type_obj?.name || order.cleaning_type_name || 'N/E'}
                            </span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order)}`}>
                            {getStatusText(order)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Tabla de espacios comunes */}
            {staff.commonSpaceOrders.length > 0 && (
              <div className="overflow-x-auto">
                <div className="flex items-center px-2 py-1 bg-green-50 border-b border-gray-200">
                  <MdMeetingRoom className="h-3 w-3 text-green-600 mr-1" aria-hidden="true" />
                  <span className="text-xs font-medium text-green-700">Espacios Comunes ({staff.commonSpaceOrders.length})</span>
                </div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Espacio
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Tiempo
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {staff.commonSpaceOrders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-2 py-1.5">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-6 px-2 bg-green-100 rounded-lg flex items-center justify-center">
                              <span className="text-xs font-medium text-green-800 truncate max-w-[80px]">
                                {order.common_space?.name || 'N/A'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          <span className="text-xs text-gray-600">
                            {order.common_space?.cleaning_time_minutes || '-'} min
                          </span>
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order)}`}>
                            {getStatusText(order)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default StaffCleaningOrdersView;
